import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Star, Award, PlaneTakeoff } from 'lucide-react';
import { motion } from 'framer-motion';
import PageSeo from '@/components/PageSeo';

export default function Press() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const storyIdeas = [
    {
      category: "Financial Innovation",
      title: "The rise of layaway booking in the travel industry",
      description: "How Defera is democratizing air travel through flexible payment solutions and no credit check booking. Explore how installment-based flight booking is making travel accessible to millions who couldn't afford upfront payments.",
      angles: [
        "The rise of 'buy now, pay later' in the travel industry",
        "How payment flexibility is changing consumer travel behavior",
        "Financial inclusion in the travel sector"
      ]
    },
    {
      category: "Technology & Innovation",
      title: "AI-Powered Travel Planning",
      description: "Defera leverages advanced technology to streamline flight booking, price protection, and trip management. From intelligent airport search to automated payment scheduling, see how technology is transforming travel planning.",
      angles: [
        "Machine learning in flight price prediction and protection",
        "Automated booking workflows reducing friction",
        "The role of AI in modern travel platforms"
      ]
    },
    {
      category: "Consumer Trends",
      title: "The Shifting Travel Landscape",
      description: "Post-pandemic travel behavior shows unprecedented demand for flexibility and affordability. Defera's price guarantee and installment options represent a new paradigm in how consumers approach travel planning.",
      angles: [
        "How Gen Z and Millennials are reshaping travel booking",
        "The demand for transparent, flexible travel options",
        "Price volatility and consumer protection in air travel"
      ]
    },
    {
      category: "Business Travel",
      title: "Corporate Travel Management Evolved",
      description: "Small and medium businesses are discovering new ways to manage team travel without breaking the bank. Defera's platform enables companies to book travel for distributed teams while maintaining budget control.",
      angles: [
        "How startups manage travel on tight budgets",
        "The rise of distributed teams and travel implications",
        "Cash flow management in corporate travel"
      ]
    },
    {
      category: "Economic Impact",
      title: "Making Travel Accessible",
      description: "Economic barriers have traditionally limited who can travel. Defera's no credit check model and installment plans are opening doors for underserved markets, from students to gig workers.",
      angles: [
        "Breaking down financial barriers to air travel",
        "Travel accessibility for students and young professionals",
        "The gig economy and flexible work-travel lifestyle"
      ]
    },
    {
      category: "Sustainability",
      title: "Smarter Travel, Lower Carbon",
      description: "Efficient booking platforms and reduced trip changes contribute to more sustainable travel patterns. Learn how Defera's price lock feature reduces last-minute bookings and associated carbon impacts.",
      angles: [
        "How payment certainty reduces travel plan changes",
        "Technology's role in sustainable travel",
        "Reducing travel waste through better planning"
      ]
    }
  ];

  const companyInfo = {
    founded: "2024",
    location: "San Francisco, CA",
    mission: "Making air travel accessible and affordable for everyone through flexible payment solutions",
    focus: "Flight booking platform with installment payment options, price protection, and seamless user experience"
  };

  return (
    <div className="min-h-screen bg-black text-white/80">
      <PageSeo path="/press" />
      <div className="mx-auto max-w-6xl px-6 py-12">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-tight">
            Press & Media
          </h1>
          <p className="text-base text-white/80 max-w-2xl leading-relaxed mb-8">
            Resources and story ideas for journalists covering innovation in travel, fintech, and consumer technology.
          </p>

          {/* Media Contact */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 inline-block">
            <div className="flex items-center gap-3 text-sm">
              <Mail className="w-5 h-5 text-[var(--accent-color)]" />
              <div>
                <div className="text-white/50 text-xs mb-1">Media Inquiries</div>
                <a 
                  href="mailto:press@defera.app" 
                  className="text-white hover:text-[var(--accent-color)] transition-colors"
                >
                  press@defera.app
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Company Quick Facts */}
        <div className="mb-16 bg-white/5 border border-white/10 rounded-2xl p-8">
          <h2 className="text-xl font-light text-white mb-6">Company Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-white/50 text-xs mb-1">Founded</div>
              <div className="text-white">{companyInfo.founded}</div>
            </div>
            <div>
              <div className="text-white/50 text-xs mb-1">Location</div>
              <div className="text-white">{companyInfo.location}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-white/50 text-xs mb-1">Mission</div>
              <div className="text-white">{companyInfo.mission}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-white/50 text-xs mb-1">Focus</div>
              <div className="text-white">{companyInfo.focus}</div>
            </div>
          </div>
        </div>

        {/* Press Kit */}
        <div className="mb-16">
          <h2 className="text-2xl font-light text-white mb-4">Press Kit</h2>
          <div className="bg-white/5 border border-white/10 rounded-xl p-8">
            <p className="text-sm text-white/80 leading-relaxed">
              For high-resolution logos, brand guidelines, company fact sheets, and additional press materials, please contact{' '}
              <a href="mailto:press@defera.app" className="text-[var(--accent-color)] hover:underline font-medium">
                press@defera.app
              </a>
            </p>
          </div>
        </div>

        {/* Story Ideas */}
        <div className="mb-16">
          <h2 className="text-2xl font-light text-white mb-2">Story Ideas</h2>
          <p className="text-sm text-white/80 mb-8 max-w-3xl">
            Defera sits at the intersection of travel, fintech, and consumer technology. Here are some angles for potential coverage:
          </p>
          
          <div className="space-y-6">
            {storyIdeas.map((story, index) => (
              <motion.div
                key={story.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-8"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-[var(--accent-color)]/20 text-[var(--accent-color)] text-xs px-3 py-1 rounded-full">
                    {story.category}
                  </div>
                </div>
                
                <h3 className="text-xl font-light text-white mb-3">
                  {story.title}
                </h3>
                
                <p className="text-sm text-white/80 leading-relaxed mb-4">
                  {story.description}
                </p>

                <div className="border-t border-white/10 pt-4 mt-4">
                  <div className="text-xs text-white/50 mb-2">Possible Angles:</div>
                  <ul className="space-y-1">
                    {story.angles.map((angle, i) => (
                      <li key={i} className="text-xs text-white/60 pl-4 relative before:content-['•'] before:absolute before:left-0">
                        {angle}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Oscar-Style Award Banners */}
        <div className="mb-12 space-y-4">
          {/* Reward System Banner */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-900/20 via-brand-900/10 to-brand-800/20 border-2 border-brand-500/30 backdrop-blur-xl">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-400/5 via-brand-400/5 to-brand-400/5" />
            <div className="relative px-6 py-4 flex items-center gap-4">
              {/* Trophy Icon */}
              <div className="relative">
                <div className="absolute inset-0 bg-brand-400/20 rounded-full blur-xl" />
                <div className="relative w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-400 via-brand-400 to-brand-500 shadow-lg shadow-brand-500/50">
                  <Award className="w-6 h-6 text-brand-900" />
                </div>
                {/* Shine Effect */}
                <div className="absolute top-0 right-0 w-3 h-3 bg-white/60 rounded-full blur-sm" />
              </div>

              {/* Text Content */}
              <div className="flex-1">
                <p className="text-sm md:text-base font-normal text-brand-100 leading-relaxed">
                  <span className="font-medium text-brand-200">Defera</span> is the world's first travel layaway service that offers a <span className="font-medium text-brand-200">reward system</span>
                </p>
                <p className="text-xs text-brand-300/60 mt-1 hidden md:block">
                  Triple dip by earning credit card points, frequent flyer miles, and Defera Miles
                </p>
              </div>

              {/* Decorative Stars */}
              <div className="hidden md:flex gap-1">
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
              </div>
            </div>
          </div>

          {/* Semi-Private Flights Banner */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-900/20 via-brand-900/10 to-brand-800/20 border-2 border-brand-500/30 backdrop-blur-xl">
            <div className="absolute inset-0 bg-gradient-to-r from-brand-400/5 via-brand-400/5 to-brand-400/5" />
            <div className="relative px-6 py-4 flex items-center gap-4">
              {/* Plane Icon */}
              <div className="relative">
                <div className="absolute inset-0 bg-brand-400/20 rounded-full blur-xl" />
                <div className="relative w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-brand-400 via-brand-400 to-brand-500 shadow-lg shadow-brand-500/50">
                  <PlaneTakeoff className="w-6 h-6 text-brand-900" />
                </div>
                {/* Shine Effect */}
                <div className="absolute top-0 right-0 w-3 h-3 bg-white/60 rounded-full blur-sm" />
              </div>

              {/* Text Content */}
              <div className="flex-1">
                <p className="text-sm md:text-base font-normal text-brand-100 leading-relaxed">
                  <span className="font-medium text-brand-200">Defera</span> is the world's first travel layaway service that offers <span className="font-medium text-brand-200">semi-private flights</span>
                </p>
                <p className="text-xs text-brand-300/60 mt-1 hidden md:block">
                  Book JSX semi-private flights with flexible payment plans
                </p>
              </div>

              {/* Decorative Stars */}
              <div className="hidden md:flex gap-1">
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
                <Star className="w-4 h-4 text-brand-400 fill-brand-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="text-center bg-white/5 border border-white/10 rounded-2xl p-12"
        >
          <h2 className="text-2xl md:text-3xl font-light text-white mb-4">
            Let's tell your story
          </h2>
          <p className="text-base text-white/80 mb-8 max-w-2xl mx-auto">
            We're always happy to connect with journalists, bloggers, and content creators. Reach out to discuss story opportunities, request interviews, or access additional resources.
          </p>
          <a
            href="mailto:press@defera.app"
            className="inline-flex items-center gap-2 bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white px-8 py-3 rounded-full font-normal transition-colors text-base"
          >
            <Mail className="w-5 h-5" />
            Contact Press Team
          </a>
        </motion.div>
      </div>
    </div>
  );
}
